import { BankAccountType, Maybe, PaymentOption } from '@app/queryTyping';

import { PaymentOptionType } from '@app/common/types/enum/paymentOptionType';

import { getFixedT } from '@app/core/i18n';

const t = getFixedT(null, null);

type PaymentOptionLabelsType = {
  [ley: string]: string;
};

type PaymentOptionsLabelsType = {
  [key: string]: PaymentOptionLabelsType;
};

const paymentOptionLabels: PaymentOptionsLabelsType = {
  transferToLoan: {
    [PaymentOptionType.Default]: (
      t(
        'loan-payment-option.custom-amount|Custom amount loan payment option (default)',
        'Custom amount',
      )
    ),
    [PaymentOptionType.PrincipalOnly]: (
      t(
        'loan-payment-option.principal-only|Principal only loan payment option',
        'Principal-only',
      )
    ),
    [PaymentOptionType.RegularPayment]: (
      t(
        'loan-payment-option.regular|Regular payment loan payment option',
        'Regular payment',
      )
    ),
    [PaymentOptionType.Minimum]: (
      t(
        'loan-payment-option.minimum| Minimum payment loan payment option',
        'Minimum payment',
      )
    ),
    [PaymentOptionType.BalancePayoff]: (
      t(
        'loan-payment-option.balance-payoff|Balance payoff loan payment option',
        'Balance payoff',
      )
    ),
    [PaymentOptionType.LoanOnDueDate]: (
      t(
        'loan-payment-option.autopay|Autopay loan payment option',
        'Autopay',
      )
    ),
  },
  transferToCreditCard: {
    [PaymentOptionType.Default]: (
      t(
        'credit-card-payment-option.custom-amount|Custom amount credit card payment option (default)',
        'Custom amount',
      )
    ),
    [PaymentOptionType.Minimum]: (
      t(
        'credit-card-payment-option.minimum|Minimum payment credit card payment option',
        'Minimum payment',
      )
    ),
    [PaymentOptionType.BalancePayoff]: (
      t(
        'credit-card-payment-option.balance-payoff|Balance payoff credit card payment option',
        'Balance payoff',
      )
    ),
    [PaymentOptionType.LastStatement]: (
      t(
        'credit-card-payment-option.last-statement|Last statement credit card payment option',
        'Statement balance',
      )
    ),
    [PaymentOptionType.LoanOnDueDate]: (
      t(
        'credit-card-payment-option.autopay|Autopay credit card payment option',
        'Autopay',
      )
    ),
  },
};

export const getPaymentOptionLabel = (
  destinationType: string | null,
  paymentOption?: string | null,
): string => {
  if (!destinationType || !paymentOption) {
    return '';
  }

  let labels: PaymentOptionLabelsType | null = null;

  /**
   * we use string values, cause we dont have exported enum in our scheme
   * on the screen accounts overview we have not enum with Loan and CreditCard string values
   * but in AccountsDropdown we have ones (BankAccountType)
   * TODO it should be fixed in the future
   */
  if (['Loan', BankAccountType.LOAN].includes(destinationType)) {
    labels = paymentOptionLabels.transferToLoan;
  } else if (['CreditCard', BankAccountType.CREDITLINE].includes(destinationType)) {
    labels = paymentOptionLabels.transferToCreditCard;
  }

  return (
    labels
      ? labels[paymentOption]
      : paymentOption
  ) || '';
};

const optionNamePlaceHolder = '{paymentOption}';

export const getDynamicAmountDescription = (paymentOption: Maybe<PaymentOption>, paymentOptionLabel: string) => {
  if (paymentOption?.isDynamicAmount
    && paymentOption?.id === PaymentOptionType.LoanOnDueDate) {
    return t(
      'dynamic-payment-amount-warning.autopay|Recurring automatic payment warning',
      'The amount of this scheduled transfer can vary. We will automatically debit the due amount on the payment due date every month. ',
    );
  }
  if (paymentOption?.isDynamicAmount) {
    const msg = t(
      'dynamic-payment-amount-warning|Dynamic payment amount warning',
      'The amount of your scheduled transfer can vary because the loan payment amount will be the current {paymentOption} amount on the date of the transfer.',
    );

    return msg.replace(optionNamePlaceHolder, paymentOptionLabel);
  }
  return null;
};
